import React, { PropsWithChildren } from "react";

import { css } from "@compiled/react";

import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { Adg4DraggableTab } from "@/components/adg4/tabs/src/draggable-tab";
import { Adg4DraggableTabList } from "@/components/adg4/tabs/src/draggable-tab-list";
import { Adg4TabPanel } from "@/components/adg4/tabs/src/tab-panel";
import { Adg4Tabs } from "@/components/adg4/tabs/src/tabs";
import TabsContent from "@/components/adg4/tabs-content/src/tabs-content";
import { wacToken } from "@/libraries/design/foundations";
import { DataEventProps } from "@/libraries/wpl/foundations/src/data-event-props";
import { Row } from "@/libraries/wpl/foundations/src/grid";
export type TabsDataType = {
    id: string;
    title: string;
    englishLabel: string;
    tabComponent: React.ReactElement<typeof TabsContent>;
}[];
export type TabsWithCardsProps = PropsWithChildren<{
    readonly h2Text: string;
    readonly tabsData: TabsDataType;
}>;

const Tab = ({
    title,
    isSelected,
    dataEventProps
}: {
    title: string;
    isSelected?: boolean;
    dataEventProps: DataEventProps;
}) => {
    return (
        <button
            css={[tabStyles, isSelected && currentStyle]}
            {...dataEventProps}
        >
            {title}
        </button>
    );
};

export const TabsWithCards = ({ h2Text, tabsData }: TabsWithCardsProps) => {
    return (
        <section id="tabs" css={sectionStyles}>
            <Adg4Tabs>
                <Row layout="8">
                    <h2 css={h2Styles}>{h2Text}</h2>
                </Row>
                <Adg4DraggableTabList
                    css={tabList}
                    rowGap="12px"
                    className="tab-list"
                >
                    {tabsData.map((tab, index) => (
                        <Adg4DraggableTab
                            key={index}
                            tabIndex={index}
                            className="round-tab"
                        >
                            <Tab
                                title={tab.title}
                                dataEventProps={{
                                    "data-product-key": "home",
                                    "data-event": "clicked",
                                    "data-event-component": "button",
                                    "data-event-container": "tabsMenu",
                                    "data-label": tab.title,
                                    "data-label-english": tab.englishLabel
                                }}
                            />
                        </Adg4DraggableTab>
                    ))}
                </Adg4DraggableTabList>
                {tabsData.map((tab, index) => (
                    <Adg4TabPanel panelIndex={index} id={tab.id} key={index}>
                        {tab.tabComponent}
                    </Adg4TabPanel>
                ))}
            </Adg4Tabs>
        </section>
    );
};

const sectionStyles = css({
    width: "100%"
});

const h2Styles = css({
    width: "100%",
    textAlign: "center",
    marginBlockEnd: 0
});

const tabList = css({
    padding: `40px 60px 37px`,

    [media.below.sm]: {
        padding: `40px 32px 30px`
    }
});

const tabStyles = css({
    "minWidth": 70,
    "backgroundColor": "#F0F1F2",
    "alignItems": "center",
    "justifyContent": "center",
    "boxSizing": "border-box",
    "display": "flex",
    "textAlign": "center",
    "textDecoration": "none",
    "height": 50,
    "borderRadius": 28,
    "paddingInlineEnd": 30,
    "paddingInlineStart": 30,
    "cursor": "pointer",
    "border": "2px solid #F0F1F2",
    "fontFamily": "var(--bxp-charlie-text-font-family)",
    "fontWeight": "400",
    "fontSize": "20px",

    ":hover": {
        boxShadow: token("elevation.shadow.raised")
    },

    ":focus": {
        outlineOffset: token("space.050"),
        outlineColor: wacToken("color.primary.black")
    }
});

const currentStyle = css({
    border: `2px solid ${wacToken("color.primary.blue")}`,
    backgroundColor: wacToken("color.extended.blue100")
});
